import React from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Row, Col, Paragraph, Container} from '@modifi/ui'

import {
  Wrapper,
  StyledContainer,
  MainContentWrapper,
  Gradient,
  Inner,
  StyledPicture,
  StyledButtonLink,
  BackgroundPicture,
  BottomCtaWrapper,
  StyledVideo,
  StyledCol,
} from './styles'
import {ctaMapping as defaultCtaMapping} from '../constants'
import LinkComponent from '../link-component'
import {StyledH1} from '../common'

const ctaMapping = {
  ...defaultCtaMapping,
  link: StyledButtonLink,
}

const propTypes = {
  headline: PropTypes.string,
  description: PropTypes.string,
  backgroundImage: PropTypes.string.isRequired,
  backgroundVideo: PropTypes.string,
  hasGradientOverlay: PropTypes.bool,
  mainCtas: PropTypes.arrayOf(
    PropTypes.shape({
      targetUrl: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      openInNewTab: PropTypes.bool.isRequired,
    }).isRequired
  ),
  bottomCtas: PropTypes.arrayOf(
    PropTypes.shape({
      targetUrl: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      openInNewTab: PropTypes.bool.isRequired,
    }).isRequired
  ),
  inverseFontColor: PropTypes.bool,
  shortHeroImage: PropTypes.bool,
  hasLandingPageForm: PropTypes.bool,
}
const defaultProps = {
  headline: undefined,
  backgroundVideo: undefined,
  hasGradientOverlay: false,
  description: undefined,
  mainCtas: [],
  bottomCtas: [],
  inverseFontColor: false,
  shortHeroImage: false,
  hasLandingPageForm: false,
}

export type HeroImageProps = InferPropTypes<typeof propTypes, typeof defaultProps>

export const HeroImage: React.FC<HeroImageProps> = ({
  headline,
  description,
  mainCtas,
  bottomCtas,
  inverseFontColor,
  backgroundImage,
  backgroundVideo,
  hasGradientOverlay,
  shortHeroImage,
  hasLandingPageForm,
}) => (
  <Wrapper
    inverseFontColor={inverseFontColor}
    shortHeroImage={shortHeroImage}
    hasLandingPageForm={hasLandingPageForm || false}
  >
    {hasGradientOverlay && <Gradient />}
    <BackgroundPicture objectFit="cover">
      {!!backgroundVideo && (
        <StyledVideo
          src={backgroundVideo}
          // @ts-expect-error type not generated
          type="video/mp4"
          autoPlay
          muted
          loop
          playsInline
          width="100%"
          poster={backgroundImage || ''}
        />
      )}
      {!backgroundVideo && !!backgroundImage && (
        <StyledPicture
          alt="Hero image"
          src={backgroundImage}
          objectFit="cover"
          id="bImage"
          isBackgroundImage
          objectPosition="100% 100%"
        />
      )}
    </BackgroundPicture>
    <StyledContainer>
      <Inner id="inner">
        <MainContentWrapper id="main-content-wrapper" shortHeroImage={shortHeroImage}>
          {headline && (
            <Row>
              <Col {...(hasLandingPageForm ? {} : {md: 10, lg: 7})}>
                <StyledH1>{headline}</StyledH1>
              </Col>
            </Row>
          )}
          {description && (
            <Row>
              <Col {...(hasLandingPageForm ? {} : {md: 10, lg: 7})}>
                <Paragraph>{description}</Paragraph>
              </Col>
            </Row>
          )}
          {mainCtas!.length > 0 && (
            <Row mt={24} mb={40}>
              {mainCtas?.map(mainCta => (
                <Col col="auto" key={mainCta?.label}>
                  <LinkComponent
                    targetUrl={mainCta!.targetUrl}
                    type={mainCta!.type}
                    label={mainCta!.label}
                    openInNewTab={mainCta!.openInNewTab}
                    ctaMapping={ctaMapping}
                  />
                </Col>
              ))}
            </Row>
          )}
        </MainContentWrapper>
      </Inner>
    </StyledContainer>
    {bottomCtas!.length > 0 && (
      <BottomCtaWrapper>
        <Container>
          <Row>
            {bottomCtas?.map(bottomCta => (
              <StyledCol col="auto" key={bottomCta!.label}>
                <LinkComponent
                  targetUrl={bottomCta.targetUrl}
                  type={bottomCta.type}
                  label={bottomCta.label}
                  openInNewTab={bottomCta.openInNewTab}
                  ctaMapping={ctaMapping}
                />
              </StyledCol>
            ))}
          </Row>
        </Container>
      </BottomCtaWrapper>
    )}
  </Wrapper>
)

HeroImage.propTypes = propTypes

HeroImage.defaultProps = defaultProps

export default HeroImage
